import React from 'react';
import AppLink from '../../../routes/app_link';
import Rating from '../../../advisor_avatar/rating/rating';
import classes from './classes.module.scss';
import previewClasses from '../advisor_preview/classes.module.scss';
import AdvisorAvatar, { advisorThemes, advisorThemesDesktopSize } from '../../../advisor_avatar';
import AdvisorModes from '../advisor_modes';
import ReadingSinceAndCountItem, { readingSinceAndCountItemThemes } from '../../../advisor/nested_views/readingSinceAndCountItem';
import ExpandedText from '../../../../../common/helpers/expanded_text';
import { AdvisorModesThemes } from '../../../../config/const';
import PriceText from '../price_text';
import { isKA, isPS } from '../../../../../common/config/utils';
import {
  isXfmProgram,
  separateThousands
} from '../../../../config/util';
import { renderPsiquicosFreeMinutesLabel } from '../nested_views_utils';

const handleLinkClickAction = ({
  e, loading, sectionSlug, updateNavigationStack
}) => {
  if (loading) {
    e.preventDefault();
    return;
  }
  if (sectionSlug) { updateNavigationStack({ dataKey: sectionSlug }); }
};

const renderLeftSideFooter = (localLiveModeMinPrice, localLiveModeMinPromoPrices, liveModes, theme, text, psiquicosFreeMinutesBadge = null) => (isKA()
  ? (
    <PriceText
      localLiveModeMinPrice={ localLiveModeMinPrice }
      localLiveModeMinPromoPrices={ localLiveModeMinPromoPrices }
      text={ text }
      theme={ theme }
      liveModes={ liveModes }
    />
  )
  : (
    <AdvisorModes
      liveModeMinPrice={ localLiveModeMinPrice }
      liveModes={ liveModes }
      localLiveModeMinPromoPrices={ localLiveModeMinPromoPrices }
      text={ text }
      theme={ theme }
      psiquicosFreeMinutesBadge={ psiquicosFreeMinutesBadge }
    />
  )
);

function StaffPickSection({
  iconUrl, title, analytics, advisor, t, onAnalyticsClick, loading, sectionSlug, updateNavigationStack, innerRef, userXfmProgram = {}
}) {
  if (!advisor) return null;

  const {
    localLiveModeMinPrice, nickname, serviceTitle, rating, staffPickText, readingsCount, yearJoined,
    liveModes, localLiveModeMinPromoPrices, slug, isNewAdvisor
  } = advisor;

  const advisorHeader = () => (
    <div className={ classes.advisorInfoHeader }>
      <div className={ classes.advisorTitle }>
        {nickname}
        <div className={ classes.advisorSubtitle }>
          {serviceTitle}
        </div>
      </div>
      <Rating rating={ rating } isNewAdvisor={ isNewAdvisor } />
    </div>
  );

  const onDescriptionExpand = (e) => {
    e.preventDefault();
  };

  const renderPsMobileReadingsBlock = () => {
    if (isNewAdvisor) {
      return (
        <ReadingSinceAndCountItem
          readingsCount={ readingsCount }
          yearJoined={ yearJoined }
          theme={ readingSinceAndCountItemThemes.staffPickMobile }
          isNewAdvisor={ isNewAdvisor }
        />
      );
    }

    return (
      <>
        <span className={ previewClasses.psiquicosReadingsCount } suppressHydrationWarning data-testid="readings-count">
          { separateThousands(readingsCount) }
        </span>
        <span className={ previewClasses.psiquicosReadingsMeta } suppressHydrationWarning>
          { t('home_page.readings_since_mobile') }
          <span suppressHydrationWarning>{ ` (${ yearJoined || '' })` }</span>
        </span>
      </>
    );
  };

  const staffPickDescription = () => (
    <div className={ classes.staffPickDescription }>
      { !isPS() && (
        <ReadingSinceAndCountItem
          readingsCount={ readingsCount }
          yearJoined={ yearJoined }
          theme={ readingSinceAndCountItemThemes.staffPickMobile }
          isNewAdvisor={ isNewAdvisor }
        />
      ) }
      <div className={ isPS() ? classes.psMobileDescriptionWrap : undefined }>
        <ExpandedText
          className={ classes.serviceTitleContainer }
          ellipsis
          limit={ 4 }
          readMoreKind="limitTextByRows"
          readMoreClick={ onDescriptionExpand }
          t={ t }
          customClasses={ isPS() ? { text: classes.psMobileDescriptionText, readMoreBtn: classes.psReadMoreBtn } : undefined }
        >
          {staffPickText}
        </ExpandedText>
        { isPS() && (
          <div className={ classes.psMobilePriceRow }>
            <PriceText
              localLiveModeMinPrice={ localLiveModeMinPrice }
              localLiveModeMinPromoPrices={ localLiveModeMinPromoPrices }
              text={ t('home_page.from') }
              liveModes={ liveModes }
              stacked
            />
          </div>
        ) }
      </div>
    </div>
  );

  const handleLinkClick = (e) => {
    if (onAnalyticsClick) { onAnalyticsClick(); }
    handleLinkClickAction({
      e, loading, sectionSlug, updateNavigationStack
    });
  };

  const psiquicosFreeMinutesBadge = React.useMemo(() => {
    if (!isPS() || !isXfmProgram({ advisorXfmProgram: advisor?.xfmProgram, userXfmProgram })) return null;
    return renderPsiquicosFreeMinutesLabel(t);
  }, [advisor?.xfmProgram?.active, userXfmProgram?.credits, t]);

  const renderPsFooterDesktop = () => (
    <div className={ classes.psFooterDesktop }>
      { renderLeftSideFooter(
        localLiveModeMinPrice,
        localLiveModeMinPromoPrices,
        liveModes,
        AdvisorModesThemes.staffPick,
        t('home_page.from'),
        psiquicosFreeMinutesBadge
      ) }
    </div>
  );

  const renderPsFooterMobile = () => (
    <>
      <div className={ classes.psFooterMobileReadingsRow }>
        <div className={ previewClasses.psiquicosReadingsColumn }>
          <div className={ previewClasses.psiquicosReadings }>
            { renderPsMobileReadingsBlock() }
          </div>
        </div>
        { psiquicosFreeMinutesBadge ? (
          <div className={ previewClasses.psiquicosFreeMinWrap }>
            { psiquicosFreeMinutesBadge }
          </div>
        ) : null }
      </div>
      <div className={ classes.psFooterMobileModes }>
        <AdvisorModes
          liveModeMinPrice={ localLiveModeMinPrice }
          liveModes={ liveModes }
          localLiveModeMinPromoPrices={ localLiveModeMinPromoPrices }
          text={ t('home_page.from') }
          theme={ AdvisorModesThemes.staffPick }
          showPrice={ false }
          psiquicosFreeMinutesBadge={ null }
          modesOnly
        />
      </div>
    </>
  );

  const footer = () => (
    <div
      className={ `${ classes.footer } ${ isKA() ? classes.v2 : '' } ${ isPS() ? classes.footerPs : '' }` }
    >
      { isPS() ? (
        <>
          { renderPsFooterDesktop() }
          { renderPsFooterMobile() }
        </>
      ) : (
        renderLeftSideFooter(
          localLiveModeMinPrice,
          localLiveModeMinPromoPrices,
          liveModes,
          AdvisorModesThemes.staffPick,
          t('home_page.from'),
          psiquicosFreeMinutesBadge
        )
      ) }
      <ReadingSinceAndCountItem
        readingsCount={ readingsCount }
        yearJoined={ yearJoined }
        theme={ isPS() ? readingSinceAndCountItemThemes.staffPickPs : readingSinceAndCountItemThemes.staffPick }
        isNewAdvisor={ isNewAdvisor }
      />
      {isKA() && (
        <AdvisorModes
          liveModes={ liveModes }
          theme={ AdvisorModesThemes.staffPick }
          showPrice={ false }
        />
      )}
    </div>
  );

  const header = () => (
    <div className={ classes.sectionHeader }>
      <img src={ iconUrl } alt="" className={ classes.sectionIcon } />
      <div className={ classes.sectionHeaderSeparator } />
      <div className={ classes.headerTitle }>{title || 'In the spotlight'}</div>
    </div>
  );

  const renderContent = () => (
    <div className={ classes.mainContentContainer } data-testid="HP-staff-pick-section" ref={ innerRef }>
      {header()}
      <AppLink onClick={ handleLinkClick } to={ { pathname: slug, state: { ...analytics } } } className={ classes.advisorViewLink }>
        <div className={ classes.advisorContainer }>
          <div className={ classes.avatarHolder }>
            <AdvisorAvatar { ...advisor } desktopSize={ advisorThemesDesktopSize.staff_pick } theme={ advisorThemes.staff_pick } analytics={ analytics } />
          </div>
          <div className={ classes.advisorInfoContainer }>
            {advisorHeader()}
            {staffPickDescription()}
            {footer()}
          </div>
        </div>
      </AppLink>
    </div>
  );

  return renderContent();
}

export default StaffPickSection;
